export type Point = { x: number; y: number }

/** Соотношение сторон SVG-карты */
export const MAP_ASPECT_RATIO = 600 / 1250

export const MIN_SCALE = 1
export const MAX_SCALE = 5
export const SCALE_STEP = 0.2
export const WHEEL_SCALE_SPEED = 0.002

export const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value))

/** Ограничение смещения карты в пределах вьюпорта */
export const clampOffset = (raw: Point, scale: number, el: HTMLElement | null): Point => {
    if (!el) return raw
    const vw = el.clientWidth
    const vh = el.clientHeight
    const contentW = vw * scale
    const contentH = vw * MAP_ASPECT_RATIO * scale
    return {
        x: clamp(raw.x, Math.min(0, vw - contentW), Math.max(0, vw - contentW)),
        y: clamp(raw.y, Math.min(0, vh - contentH), Math.max(0, vh - contentH)),
    }
}

/** Вычисление масштаба и смещения при зуме к точке */
export const computeZoom = (
    nextScale: number,
    prevScale: number,
    prevOffset: Point,
    viewportEl: HTMLElement | null,
    clientPoint?: Point,
): { scale: number; offset: Point } => {
    const clamped = clamp(nextScale, MIN_SCALE, MAX_SCALE)

    if (!clientPoint || !viewportEl) {
        return { scale: clamped, offset: prevOffset }
    }

    const rect = viewportEl.getBoundingClientRect()
    const px = clientPoint.x - rect.left
    const py = clientPoint.y - rect.top

    const ratio = clamped / prevScale
    const offset = clampOffset(
        {
            x: px - (px - prevOffset.x) * ratio,
            y: py - (py - prevOffset.y) * ratio,
        },
        clamped,
        viewportEl,
    )

    return { scale: clamped, offset }
}

/** Центр вьюпорта в клиентских координатах */
export const getViewportCenter = (el: HTMLElement): Point => {
    const rect = el.getBoundingClientRect()
    return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
    }
}

/** Преобразование deltaY колёсика мыши в значение масштаба */
export const wheelDeltaToScale = (event: WheelEvent, currentScale: number): number => {
    const mode = event.deltaMode
    const multiplier = mode === 1 ? 16 : mode === 2 ? 320 : 1
    const deltaY = event.deltaY * multiplier
    return clamp(currentScale * Math.exp(-deltaY * WHEEL_SCALE_SPEED), MIN_SCALE, MAX_SCALE)
}

/** Форматирование мощности из кВт в МВт */
export const formatVolume = (
    kw: number | undefined,
    locale: 'ru' | 'en',
    unitLabel: string,
): string => {
    if (kw === undefined || kw === null) {
        return '—'
    }

    const numberLocale = locale === 'ru' ? 'ru-RU' : 'en-US'
    return `${(kw / 1000).toLocaleString(numberLocale, { maximumFractionDigits: 2 })} ${unitLabel}`
}

export const formatVolumeInteger = (
    kw: number | undefined,
    locale: 'ru' | 'en',
    unitLabel: string,
): string => {
    if (kw === undefined || kw === null) {
        return '—'
    }

    const numberLocale = locale === 'ru' ? 'ru-RU' : 'en-US'
    return `${(kw / 1000).toLocaleString(numberLocale, { maximumFractionDigits: 0 })} ${unitLabel}`
}